import { z } from 'zod';

/**
 * The store's catalogue: what may be sold, for how much, and where.
 *
 * ## The client sends an id and nothing else
 *
 * Price, currency, Cash amount and VIP duration are read from THIS table by the
 * server. A request that could state a price could state one cent; a request
 * that could state a Cash amount could state a million. The wire schema for an
 * order is `.strict()` and carries a `productId` and an idempotency key.
 *
 * ## Money is integer centavos
 *
 * `priceCents`, never reais as a float. `19.90` is not representable and a
 * store whose totals do not add up is a store somebody will be right to
 * complain about - the same rule the rest of this economy already follows.
 *
 * ## Versioned, because an order outlives the catalogue
 *
 * A product whose price or benefit changes materially gets a new `version` (or
 * a new id). Every order stores a SNAPSHOT of what was sold, so a receipt from
 * last month stays explicable after the table moves. `version` is what makes
 * the two comparable.
 *
 * ## Listed is not the same as buyable
 *
 * Eight approved products live here. Being listed does NOT mean anything can be
 * bought: purchasing needs `STORE_ENABLED` and a configured provider, and the
 * catalogue route reports `purchasingEnabled: false` while either is missing.
 * The two `dev_` entries are locked out of production by `environments` and by
 * a test.
 */

/** What a product delivers. Nothing else is sellable. */
export const STORE_PRODUCT_KINDS = ['cash', 'vip'] as const;
export type StoreProductKind = (typeof STORE_PRODUCT_KINDS)[number];

/**
 * The only currency this store accepts.
 *
 * A single-element union rather than a free string: adding a second currency is
 * a decision about tax, pricing and settlement, not a value somebody types.
 */
export const STORE_CURRENCIES = ['BRL'] as const;
export type StoreCurrency = (typeof STORE_CURRENCIES)[number];

/** Where a product may be offered. The same four names the API environment uses. */
export const STORE_ENVIRONMENTS = ['development', 'test', 'staging', 'production'] as const;
export type StoreEnvironment = (typeof STORE_ENVIRONMENTS)[number];

export interface StoreProduct {
  /** Stable across price changes. A material change gets a new version or id. */
  readonly productId: string;
  /** Bumped whenever price, Cash or VIP duration changes materially. */
  readonly version: number;
  readonly kind: StoreProductKind;
  readonly name: string;
  readonly description: string;
  /**
   * The NORMAL price, in integer centavos of Real. Never a float.
   *
   * The list price, not necessarily what is charged today: while the closed
   * alpha's promotion is running the effective price is
   * {@link StoreProduct.promoPriceCents}. Resolving that is the SERVER's job -
   * see `effectivePriceCents`.
   */
  readonly priceCents: number;
  /**
   * The promotional price, when the product has one. Exactly half the normal.
   *
   * Written down rather than derived so the table can be read at a glance, and
   * validated by `catalogProblems` so a typo cannot quietly become a 40% or a
   * 60% discount labelled "50% OFF".
   */
  readonly promoPriceCents?: number;
  readonly currency: StoreCurrency;
  /** Cash delivered. Zero for a VIP product. */
  readonly cashAmount: bigint;
  /** VIP delivered, in whole seconds. Zero for a Cash product. */
  readonly vipSeconds: number;
  /** An inactive product is not sold and not listed, but old orders still name it. */
  readonly active: boolean;
  /**
   * Retired from sale, kept so history stays explicable.
   *
   * A legacy entry is never listed and never orderable - `active: false` sees
   * to that - but deleting the definition would leave old orders naming an id
   * nothing in the codebase has heard of. The receipt itself comes from the
   * order's frozen snapshot; this exists so a reader can still ask "what was
   * `cash_50_v1`?" and get an answer.
   */
  readonly legacy?: boolean;
  /** Where it may be offered. A production listing needs 'production' here. */
  readonly environments: readonly StoreEnvironment[];
  /** Ascending. Ties break by productId, so the order is total and stable. */
  readonly displayOrder: number;
}

export const storeProductSchema = z.object({
  productId: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9_]+$/),
  version: z.number().int().min(1),
  kind: z.enum(STORE_PRODUCT_KINDS),
  name: z.string().min(1).max(80),
  description: z.string().min(1).max(240),
  priceCents: z.number().int().min(1),
  promoPriceCents: z.number().int().min(1).optional(),
  currency: z.enum(STORE_CURRENCIES),
  cashAmount: z.bigint().min(0n),
  vipSeconds: z.number().int().min(0),
  active: z.boolean(),
  legacy: z.boolean().optional(),
  environments: z.array(z.enum(STORE_ENVIRONMENTS)).min(1),
  displayOrder: z.number().int().min(0),
});

/**
 * The prefix every non-production product carries.
 *
 * Belt and braces beside `environments`: a fixture that somehow reached a
 * production list is still recognisable by its id, and a test asserts that no
 * id starting with this can ever be returned for production.
 */
export const STORE_DEV_PRODUCT_PREFIX = 'dev_';

/** Every environment. An approved product is the same product everywhere. */
const EVERYWHERE = Object.freeze([
  'development',
  'test',
  'staging',
  'production',
] as const) as readonly StoreEnvironment[];

/**
 * A Cash product, written once so the packages cannot drift apart.
 *
 * The promotional price is passed and CHECKED rather than computed, because a
 * table somebody can read is worth more than one nobody can audit - and
 * `catalogProblems` refuses any row where it is not exactly half.
 */
function cashProduct(
  productId: string,
  name: string,
  cash: number,
  normalCents: number,
  promoCents: number,
  displayOrder: number,
): StoreProduct {
  return Object.freeze({
    productId,
    version: 1,
    kind: 'cash',
    name,
    description: `Adiciona ${cash.toLocaleString('pt-BR')} Cash à sua conta.`,
    priceCents: normalCents,
    promoPriceCents: promoCents,
    currency: 'BRL',
    cashAmount: BigInt(cash),
    vipSeconds: 0,
    active: true,
    environments: EVERYWHERE,
    displayOrder,
  }) as StoreProduct;
}

/** A VIP product. Duration in days, converted to the seconds the grant uses. */
function vipProduct(
  productId: string,
  days: number,
  normalCents: number,
  promoCents: number,
  displayOrder: number,
): StoreProduct {
  return Object.freeze({
    productId,
    version: 1,
    kind: 'vip',
    name: `VIP — ${days} dias`,
    description: `Ativa ou estende o VIP por ${days} dias.`,
    priceCents: normalCents,
    promoPriceCents: promoCents,
    currency: 'BRL',
    cashAmount: 0n,
    vipSeconds: days * 24 * 60 * 60,
    active: true,
    environments: EVERYWHERE,
    displayOrder,
  }) as StoreProduct;
}

/**
 * What a product costs RIGHT NOW.
 *
 * The one place an effective price is decided, and it takes the promotion's
 * state rather than a clock - so nothing can accidentally price from the
 * machine it happens to be running on, and a test can price any instant it
 * likes without moving time.
 */
export function effectivePriceCents(product: StoreProduct, promotionActive: boolean): number {
  return promotionActive && product.promoPriceCents !== undefined
    ? product.promoPriceCents
    : product.priceCents;
}

/**
 * The approved commercial catalogue.
 *
 * Eight products, five Cash and three VIP, at the prices the design signed off.
 * The Cash amounts are FINAL: there is no bonus applied on top, no promotional
 * multiplier and no VIP discount. What the card says is what lands.
 *
 * Every id carries `_v1`. A change to a price or to a quantity is a change to
 * what was sold, and it gets `_v2` rather than an edit - orders already placed
 * keep their own snapshot either way, but the id is what makes two receipts
 * comparable at a glance.
 *
 * Being listed here does NOT mean anything can be bought: purchasing needs
 * `STORE_ENABLED` and a configured provider, and neither exists yet.
 */
export const PRODUCTION_STORE_PRODUCTS: readonly StoreProduct[] = Object.freeze([
  cashProduct('cash_100', 'Pacote Inicial', 100, 1_980, 990, 10),
  cashProduct('cash_275', 'Pacote Órbita', 275, 4_980, 2_490, 20),
  cashProduct('cash_600', 'Pacote Nova', 600, 9_980, 4_990, 30),
  cashProduct('cash_1300', 'Pacote Singularidade', 1_300, 19_980, 9_990, 40),
  vipProduct('vip_7_days', 7, 1_980, 990, 45),
  vipProduct('vip_30_days', 30, 4_980, 2_490, 50),
]);

/**
 * The catalogue's own version. Bumped when the SET of products changes.
 *
 * Distinct from a product's `version`, which tracks one entry's price and
 * benefit. This one answers "which shop was this?" - useful to an interface
 * deciding whether its cached list is stale, and to anybody reading an old
 * report and wondering what was on sale at the time.
 */
export const STORE_CATALOG_VERSION = 3;

/**
 * Products that were once sold and never will be again.
 *
 * Retired, not deleted. `active: false` keeps them out of every catalogue and
 * out of `storeProductFor`, so no new order can name one - but an order placed
 * against `cash_50_v1` still exists, still has a receipt, and still has to be
 * explicable. The receipt comes from the order's own frozen snapshot; these
 * definitions exist so the id itself is not an orphan.
 *
 * Their ids are never reused. A future `cash_50_v2` would be a different
 * product; reusing `cash_50_v1` would silently reinterpret somebody's history.
 */
export const LEGACY_STORE_PRODUCTS: readonly StoreProduct[] = Object.freeze(
  (
    [
      ['cash_50_v1', '50 Cash', 'cash', 50, 490],
      ['cash_105_v1', '105 Cash', 'cash', 105, 990],
      ['cash_550_v1', '550 Cash', 'cash', 550, 4_990],
      ['cash_1150_v1', '1.150 Cash', 'cash', 1_150, 9_990],
      ['cash_2400_v1', '2.400 Cash', 'cash', 2_400, 19_990],
      ['vip_7d_v1', 'VIP 7 dias', 'vip', 7, 690],
      ['vip_30d_v1', 'VIP 30 dias', 'vip', 30, 1_990],
      ['vip_90d_v1', 'VIP 90 dias', 'vip', 90, 4_990],
    ] as const
  ).map(([productId, name, kind, amount, priceCents]) =>
    Object.freeze({
      productId,
      version: 1,
      kind,
      name,
      description: 'Produto encerrado. Mantido apenas para o histórico de compras.',
      priceCents,
      currency: 'BRL',
      cashAmount: kind === 'cash' ? BigInt(amount) : 0n,
      vipSeconds: kind === 'vip' ? amount * 24 * 60 * 60 : 0,
      // Never sold again, by the same flag every inactive product uses.
      active: false,
      legacy: true,
      environments: EVERYWHERE,
      displayOrder: 900,
    }),
  ) as StoreProduct[],
);

/**
 * The product that exists so the Pix integration can be proved end to end.
 *
 * Mercado Pago's Orders test procedure is not free-form: it prescribes a total
 * of exactly **R$ 50,00** together with a specific test payer, and the approved
 * commercial catalogue has no product at that price (R$ 49,90 is the closest,
 * and "close" is not what a prescribed test value means). Rather than bend a
 * real price to fit a test - which would change what a player is charged - the
 * test gets its own product.
 *
 * It delivers **1 Cash**, which is the smallest observable grant: the point is
 * to watch a benefit arrive exactly once, not to fund an account.
 *
 * `development` and `test` only, and it carries {@link STORE_DEV_PRODUCT_PREFIX}
 * - so it is locked out of production twice over, and `catalogProblems` refuses
 * a table that ever offers it there.
 */
export const SANDBOX_VALIDATION_PRODUCT_ID = 'dev_pix_validation_v1';

/**
 * Two extra products that exist only where nothing is real.
 *
 * TEST only - not even development. They are cheap enough to pay for by hand
 * against a sandbox, which the approved prices deliberately are not, and that
 * is worth having in an automated journey. It is not worth putting a card
 * reading "[DEV] Cash de teste" beside the real ones on a developer's screen.
 *
 * Locked out of production two ways: `environments` excludes it, and the id
 * carries {@link STORE_DEV_PRODUCT_PREFIX}.
 */
export const DEVELOPMENT_STORE_PRODUCTS: readonly StoreProduct[] = Object.freeze([
  Object.freeze({
    productId: SANDBOX_VALIDATION_PRODUCT_ID,
    version: 1,
    kind: 'cash',
    name: 'VALIDAÇÃO PIX SANDBOX',
    description:
      'Produto interno de validação do Pix em sandbox. Não é uma oferta comercial e não existe em produção.',
    // R$ 50,00 exactly, as the Mercado Pago Orders test procedure prescribes.
    priceCents: 5_000,
    currency: 'BRL',
    // The smallest grant that can be observed arriving exactly once.
    cashAmount: 1n,
    vipSeconds: 0,
    active: true,
    environments: Object.freeze(['development', 'test'] as const),
    displayOrder: 0,
  }),
  Object.freeze({
    productId: 'dev_cash_small',
    version: 1,
    kind: 'cash',
    name: '[DEV] Cash de teste',
    description: 'Produto de desenvolvimento. Preço e quantidade não são definitivos.',
    priceCents: 100,
    currency: 'BRL',
    cashAmount: 100n,
    vipSeconds: 0,
    active: true,
    environments: Object.freeze(['test'] as const),
    displayOrder: 0,
  }),
  Object.freeze({
    productId: 'dev_vip_week',
    version: 1,
    kind: 'vip',
    name: '[DEV] VIP de teste',
    description: 'Produto de desenvolvimento. Preço e duração não são definitivos.',
    priceCents: 200,
    currency: 'BRL',
    cashAmount: 0n,
    vipSeconds: 7 * 24 * 60 * 60,
    active: true,
    environments: Object.freeze(['test'] as const),
    displayOrder: 1,
  }),
] as StoreProduct[]);

/** Everything that exists, in every environment. Not a catalogue for anybody. */
export const ALL_STORE_PRODUCTS: readonly StoreProduct[] = Object.freeze([
  ...PRODUCTION_STORE_PRODUCTS,
  ...DEVELOPMENT_STORE_PRODUCTS,
  ...LEGACY_STORE_PRODUCTS,
]);

/**
 * Any definition this codebase has ever sold, active or not.
 *
 * For explaining history, and for nothing else. It is deliberately NOT what
 * `storeProductFor` consults: looking a product up in order to sell it must
 * never find a retired one.
 */
export function storeProductDefinition(productId: string): StoreProduct | null {
  return ALL_STORE_PRODUCTS.find((product) => product.productId === productId) ?? null;
}

/**
 * Sorted by display order, then by id.
 *
 * The tie-break makes the order TOTAL: two products sharing a display order
 * would otherwise appear in whatever sequence the array happened to hold, and a
 * catalogue that reorders itself between requests is a catalogue that looks
 * broken.
 */
function inDisplayOrder(products: readonly StoreProduct[]): readonly StoreProduct[] {
  return [...products].sort(
    (a, b) => a.displayOrder - b.displayOrder || (a.productId < b.productId ? -1 : 1),
  );
}

/**
 * What may be sold in this environment.
 *
 * The ONE function anything is allowed to build a catalogue from. It filters on
 * `active` and on `environments`, so a development fixture cannot appear in
 * production even if somebody adds it to the wrong array - and an inactive
 * product disappears from the shop while every order that names it stays
 * readable.
 */
export function storeCatalogFor(environment: StoreEnvironment): readonly StoreProduct[] {
  return inDisplayOrder(
    ALL_STORE_PRODUCTS.filter(
      (product) => product.active && product.environments.includes(environment),
    ),
  );
}

/**
 * One product, resolved for THIS environment, or null.
 *
 * Environment-scoped on purpose: looking a product up globally and checking the
 * environment afterwards is the shape that eventually forgets to check.
 */
export function storeProductFor(
  productId: string,
  environment: StoreEnvironment,
): StoreProduct | null {
  return storeCatalogFor(environment).find((product) => product.productId === productId) ?? null;
}

/**
 * The snapshot an order keeps, so a receipt survives the catalogue changing.
 *
 * `cashAmount` is a decimal STRING for the same reason every balance is: it is
 * a `bigint` in the domain and a JSON number is a double to every client and to
 * most tooling in between.
 */
export interface StoreProductSnapshot {
  readonly productId: string;
  readonly version: number;
  readonly kind: StoreProductKind;
  readonly name: string;
  readonly priceCents: number;
  readonly currency: StoreCurrency;
  readonly cashAmount: string;
  readonly vipSeconds: number;
}

export const storeProductSnapshotSchema = z.object({
  productId: z.string().min(1),
  version: z.number().int().min(1),
  kind: z.enum(STORE_PRODUCT_KINDS),
  name: z.string().min(1),
  priceCents: z.number().int().min(1),
  currency: z.enum(STORE_CURRENCIES),
  cashAmount: z.string().regex(/^\d+$/),
  vipSeconds: z.number().int().min(0),
});

export function snapshotOf(product: StoreProduct): StoreProductSnapshot {
  return {
    productId: product.productId,
    version: product.version,
    kind: product.kind,
    name: product.name,
    priceCents: product.priceCents,
    currency: product.currency,
    cashAmount: product.cashAmount.toString(10),
    vipSeconds: product.vipSeconds,
  };
}

/**
 * Does a product deliver anything at all?
 *
 * A row with no Cash and no VIP is a product somebody could pay for and receive
 * nothing from. Asserted where the catalogue is validated, not discovered by a
 * player.
 */
export function deliversSomething(product: StoreProduct): boolean {
  return product.cashAmount > 0n || product.vipSeconds > 0;
}

/**
 * What an order actually delivers, as a closed alternative.
 *
 * Cash or VIP, never both and never neither. The settlement reads THIS rather
 * than picking two fields off a snapshot and hoping they agree: a snapshot
 * claiming 100 Cash *and* 30 days is not a product this store has ever sold, so
 * it is not something to deliver half of - it is something to refuse.
 */
export type StoreBenefit =
  | { readonly kind: 'cash'; readonly cashAmount: bigint }
  | { readonly kind: 'vip'; readonly vipSeconds: number };

/**
 * The benefit frozen on an order, or null if it is not one we recognise.
 *
 * Null is the SAFE answer and the caller must treat it as one: nothing is
 * granted, no Cash is invented and no VIP day is guessed at. An order whose
 * snapshot is missing, malformed or internally inconsistent goes down the
 * review path that already exists rather than being interpreted generously.
 *
 * Deliberately does NOT consult the catalogue. A product's Cash amount may have
 * changed since the order was placed, and reinterpreting an old receipt against
 * today's table is how somebody gets paid the wrong amount years later.
 */
export function resolveStoreBenefit(snapshot: unknown): StoreBenefit | null {
  const parsed = storeProductSnapshotSchema.safeParse(snapshot);
  if (!parsed.success) {
    return null;
  }
  const { kind, vipSeconds } = parsed.data;
  const cashAmount = BigInt(parsed.data.cashAmount);
  if (kind === 'cash') {
    return cashAmount > 0n && vipSeconds === 0 ? { kind: 'cash', cashAmount } : null;
  }
  return vipSeconds > 0 && cashAmount === 0n ? { kind: 'vip', vipSeconds } : null;
}

/**
 * Every way a catalogue entry can be wrong, as a list of reasons.
 *
 * Returned rather than thrown, so a test can name each rule and the service can
 * refuse to start with a bad table rather than selling something malformed.
 */
export function catalogProblems(products: readonly StoreProduct[]): readonly string[] {
  const problems: string[] = [];
  const seen = new Map<string, number>();
  for (const product of products) {
    const parsed = storeProductSchema.safeParse(product);
    if (!parsed.success) {
      problems.push(`${product.productId}: ${parsed.error.issues[0]?.message ?? 'invalid'}`);
      continue;
    }
    const previous = seen.get(product.productId);
    if (previous !== undefined) {
      problems.push(`${product.productId}: duplicated id`);
    }
    seen.set(product.productId, product.version);
    if (!deliversSomething(product)) {
      problems.push(`${product.productId}: delivers neither Cash nor VIP`);
    }
    if (product.kind === 'cash' && product.cashAmount <= 0n) {
      problems.push(`${product.productId}: a cash product with no Cash`);
    }
    if (product.kind === 'vip' && product.vipSeconds <= 0) {
      problems.push(`${product.productId}: a vip product with no VIP`);
    }
    if (
      product.environments.includes('production') &&
      product.productId.startsWith(STORE_DEV_PRODUCT_PREFIX)
    ) {
      problems.push(`${product.productId}: a development fixture offered in production`);
    }
    /*
     * The discount has to be the discount it claims.
     *
     * "50% OFF" over a price invented for the occasion is a lie a shop tells
     * once, and a typo here would be exactly that - silently. Exact halves
     * only: an odd normal price has no exact half in centavos and is refused
     * rather than rounded.
     */
    if (product.promoPriceCents !== undefined && product.promoPriceCents * 2 !== product.priceCents)
      problems.push(`${product.productId}: the promotional price is not exactly half the normal`);
  }
  return problems;
}
